// src/features/game/components/TVDisplay.tsx

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  fetchGameDetails,
} from '../services/gameService';

import {
  subscribeToGame,
} from '../services/gameSseService';

import type {
  GameStatus,
  PlayGame,
  TVGameData,
} from '../types/game.types';

interface TVDisplayProps {
  playGameId: number;
}

type SseGameUpdate =
  Partial<TVGameData> & {
    gameStatus?: GameStatus;

    currentBlind?: {
      level: number;
      smallBlind: number;
      bigBlind: number;
      ante: number;
      isBreak?: boolean;
    };

    prizes?: TVGameData['prizes'];
  };

interface StatItemProps {
  label: string;
  value: string;
}

const formatTime = (
  seconds: number,
): string => {
  const safeSeconds =
    Math.max(
      0,
      seconds,
    );

  const mins =
    Math.floor(
      safeSeconds / 60,
    );

  const secs =
    safeSeconds % 60;

  return `${String(
    mins,
  ).padStart(
    2,
    '0',
  )}:${String(
    secs,
  ).padStart(
    2,
    '0',
  )}`;
};

const formatNumber = (
  value: number,
): string => {
  return value.toLocaleString(
    'ko-KR',
  );
};

const convertToTVGameData = (
  game: PlayGame,
): TVGameData => ({
  playGameId:
    game.playGameId,

  gameName:
    game.gameName,

  gameSubtitle:
    game.gameSubtitle,

  currentLevel:
    game.currentLevel ??
    0,

  levelRemainingSeconds:
    game.levelRemainingSeconds ??
    0,

  nextBreakSeconds:
    game.nextBreakSeconds,

  currentBlind:
    game.currentBlind ?? {
      level: 0,
      smallBlind: 0,
      bigBlind: 0,
      ante: 0,
    },

  nextBlind:
    game.nextBlind,

  nowPeople:
    game.nowPeople,

  totalPeople:
    game.totalPeople,

  rebuyinCount:
    game.rebuyinCount,

  earlyAmount:
    game.earlyAmount,

  earlyCount:
    game.earlyCount,

  totalStack:
    game.totalStack,

  averageStack:
    game.averageStack,

  prizes:
    game.prizes ?? [],

  startAt:
    game.startAt ?? '',
});

const StatItem:
  React.FC<StatItemProps> = ({
    label,
    value,
  }) => {
  return (
    <div className="py-6">
      <div
        className="
          mb-2
          text-lg
          font-semibold
          uppercase
          tracking-[0.14em]
          text-gray-400
        "
      >
        {label}
      </div>

      <div
        className="
          whitespace-nowrap
          text-5xl
          font-semibold
          tabular-nums
          text-white
        "
      >
        {value}
      </div>
    </div>
  );
};

const TVDisplay:
  React.FC<TVDisplayProps> = ({
    playGameId,
  }) => {
  const [
    gameData,
    setGameData,
  ] =
    useState<TVGameData | null>(
      null,
    );

  const [
    remainingSeconds,
    setRemainingSeconds,
  ] =
    useState<number>(
      0,
    );

  const [
    localNextBreakSeconds,
    setLocalNextBreakSeconds,
  ] =
    useState<number | null>(
      null,
    );

  const [
    gameStatus,
    setGameStatus,
  ] =
    useState<GameStatus>(
      'RUNNING',
    );

  const [
    isBreak,
    setIsBreak,
  ] =
    useState<boolean>(
      false,
    );

  const gameDataRef =
    useRef<TVGameData | null>(
      null,
    );

  const gameStatusRef =
    useRef<GameStatus>(
      'RUNNING',
    );

  const prevRemainingSecondsRef =
    useRef<number>(
      0,
    );

  const prevLevelRef =
    useRef<number>(
      0,
    );

  const fiveSecondAudioRef =
    useRef<HTMLAudioElement | null>(
      null,
    );

  const levelUpAudioRef =
    useRef<HTMLAudioElement | null>(
      null,
    );

  useEffect(() => {
    gameDataRef.current =
      gameData;
  }, [
    gameData,
  ]);

  useEffect(() => {
    gameStatusRef.current =
      gameStatus;
  }, [
    gameStatus,
  ]);

  useEffect(() => {
    fiveSecondAudioRef.current =
      new Audio(
        '/count_5_seconds.mp3',
      );

    levelUpAudioRef.current =
      new Audio(
        '/next_level.mp3',
      );

    return () => {
      fiveSecondAudioRef.current
        ?.pause();

      levelUpAudioRef.current
        ?.pause();

      fiveSecondAudioRef.current =
        null;

      levelUpAudioRef.current =
        null;
    };
  }, []);

  useEffect(() => {
    if (
      gameStatus ===
        'PAUSED' ||
      gameStatus ===
        'FINISHED' ||
      isBreak
    ) {
      return;
    }

    if (
      remainingSeconds ===
        5 &&
      prevRemainingSecondsRef.current ===
        6
    ) {
      void fiveSecondAudioRef.current
        ?.play()
        .catch(
          (
            error:
              unknown,
          ) => {
            console.error(
              '5초 경고음 재생 실패:',
              error,
            );
          },
        );
    }

    prevRemainingSecondsRef.current =
      remainingSeconds;
  }, [
    remainingSeconds,
    gameStatus,
    isBreak,
  ]);

  useEffect(() => {
    if (!gameData) {
      return;
    }

    if (
      gameStatus ===
        'PAUSED' ||
      gameStatus ===
        'FINISHED' ||
      isBreak
    ) {
      return;
    }

    const currentLevel =
      gameData.currentLevel;

    if (
      prevLevelRef.current >
        0 &&
      currentLevel >
        prevLevelRef.current
    ) {
      void levelUpAudioRef.current
        ?.play()
        .catch(
          (
            error:
              unknown,
          ) => {
            console.error(
              '레벨 업 음성 재생 실패:',
              error,
            );
          },
        );
    }

    prevLevelRef.current =
      currentLevel;
  }, [
    gameData,
    gameStatus,
    isBreak,
  ]);

  useEffect(() => {
    let isActive =
      true;

    const loadInitialData =
      async (): Promise<void> => {
        try {
          const game =
            await fetchGameDetails(
              playGameId,
            );

          if (!isActive) {
            return;
          }

          const tvData =
            convertToTVGameData(
              game,
            );

          setGameData(
            tvData,
          );

          setRemainingSeconds(
            tvData.levelRemainingSeconds,
          );

          setLocalNextBreakSeconds(
            tvData.nextBreakSeconds,
          );

          const status =
            game.gameStatus ??
            'RUNNING';

          setGameStatus(
            status,
          );

          if (
            status ===
            'PAUSED'
          ) {
            setIsBreak(
              false,
            );
          } else {
            const currentBlind =
              game.currentBlind as
                | (typeof game.currentBlind & {
                    isBreak?: boolean;
                  })
                | null;

            setIsBreak(
              currentBlind
                ?.isBreak ??
                false,
            );
          }
        } catch (
          error:
            unknown
        ) {
          console.error(
            'TV 초기 게임 데이터 조회 실패:',
            error,
          );
        }
      };

    void loadInitialData();

    return () => {
      isActive =
        false;
    };
  }, [
    playGameId,
  ]);

  useEffect(() => {
    const unsubscribe =
      subscribeToGame(
        playGameId,

        async (
          data:
            SseGameUpdate,
        ) => {
          if (
            data.prizes
          ) {
            setGameData(
              (
                previous,
              ) => {
                if (
                  !previous
                ) {
                  return previous;
                }

                return {
                  ...previous,
                  ...data,
                };
              },
            );
          } else {
            try {
              const game =
                await fetchGameDetails(
                  playGameId,
                );

              const tvData =
                convertToTVGameData(
                  game,
                );

              setGameData(
                tvData,
              );

              const newStatus =
                game.gameStatus ??
                'RUNNING';

              setGameStatus(
                newStatus,
              );

              if (
                newStatus ===
                'PAUSED'
              ) {
                setIsBreak(
                  false,
                );
              } else {
                setRemainingSeconds(
                  tvData.levelRemainingSeconds,
                );

                setLocalNextBreakSeconds(
                  tvData.nextBreakSeconds,
                );

                const currentBlind =
                  game.currentBlind as
                    | (typeof game.currentBlind & {
                        isBreak?: boolean;
                      })
                    | null;

                setIsBreak(
                  currentBlind
                    ?.isBreak ??
                    false,
                );
              }
            } catch (
              error:
                unknown
            ) {
              console.error(
                'TV 게임 데이터 재조회 실패:',
                error,
              );
            }
          }

          if (
            data.gameStatus
          ) {
            setGameStatus(
              data.gameStatus,
            );

            if (
              data.gameStatus ===
              'PAUSED'
            ) {
              setIsBreak(
                false,
              );
            }
          }

          if (
            data.levelRemainingSeconds !==
              undefined &&
            data.gameStatus !==
              'PAUSED'
          ) {
            setRemainingSeconds(
              data.levelRemainingSeconds,
            );
          }

          if (
            data.nextBreakSeconds !==
              undefined &&
            data.gameStatus !==
              'PAUSED'
          ) {
            setLocalNextBreakSeconds(
              data.nextBreakSeconds,
            );
          }

          if (
            data.gameStatus !==
              'PAUSED' &&
            data.currentBlind
          ) {
            setIsBreak(
              data.currentBlind
                .isBreak ??
                false,
            );
          }
        },

        (
          error,
        ) => {
          console.error(
            'TV SSE 연결 오류:',
            error,
          );
        },
      );

    return () => {
      unsubscribe();
    };
  }, [
    playGameId,
  ]);

  useEffect(() => {
    if (
      gameStatus ===
        'PAUSED' ||
      gameStatus ===
        'FINISHED'
    ) {
      return;
    }

    const interval =
      window.setInterval(
        () => {
          setRemainingSeconds(
            (
              previous,
            ) =>
              Math.max(
                0,
                previous -
                  1,
              ),
          );

          setLocalNextBreakSeconds(
            (
              previous,
            ) =>
              previous !==
                null &&
              previous >
                0
                ? previous -
                  1
                : previous,
          );
        },
        1000,
      );

    return () => {
      window.clearInterval(
        interval,
      );
    };
  }, [
    gameStatus,
  ]);

  if (!gameData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#111214]">
        <div className="text-3xl font-medium text-gray-300">
          게임 정보를 불러오는 중...
        </div>
      </div>
    );
  }

  const getElapsedTime =
    (): string => {
      if (
        !gameData.startAt
      ) {
        return '00:00:00';
      }

      const start =
        new Date(
          gameData.startAt,
        ).getTime();

      const elapsed =
        Math.max(
          0,
          Math.floor(
            (
              Date.now() -
              start
            ) /
              1000,
          ),
        );

      const hours =
        Math.floor(
          elapsed / 3600,
        );

      const mins =
        Math.floor(
          (
            elapsed %
            3600
          ) /
            60,
        );

      const secs =
        elapsed % 60;

      return `${String(
        hours,
      ).padStart(
        2,
        '0',
      )}:${String(
        mins,
      ).padStart(
        2,
        '0',
      )}:${String(
        secs,
      ).padStart(
        2,
        '0',
      )}`;
    };

  const gameTitle =
    gameData.gameSubtitle ||
    gameData.gameName;

  const isPaused =
    gameStatus ===
    'PAUSED';

  const isFinished =
    gameStatus ===
    'FINISHED';

  return (
    <div className="h-screen overflow-hidden bg-[#111214] text-white">
      <div className="flex h-full flex-col">

        {/* Header */}
        <header
          className="
            relative
            flex
            h-24
            shrink-0
            items-center
            border-b
            border-white/10
            px-10
          "
        >
          {/* Logo */}
          <div className="flex items-center gap-5">
            <img
              src="/login.png"
              alt="La Poit"
              className="h-14 w-auto object-contain"
            />

            <div className="h-8 w-px bg-white/20" />
          </div>

          {/* 정확한 화면 중앙 제목 */}
          <h1
            className="
              absolute
              left-1/2
              top-1/2
              max-w-[65vw]
              -translate-x-1/2
              -translate-y-1/2
              truncate
              whitespace-nowrap
              text-center
              text-6xl
              font-bold
              tracking-tight
              text-white
            "
          >
            {gameTitle}
          </h1>
        </header>

        {/* Main */}
        <div
          className="
            grid
            min-h-0
            flex-1
            grid-cols-[290px_minmax(0,1fr)_340px]
          "
        >
          {/* Prize */}
          <aside className="border-r border-white/10 p-7">
            <div className="mb-7">
              <div className="text-lg font-semibold uppercase tracking-[0.16em] text-gray-400">
                Prize
              </div>

              <div className="mt-1 text-3xl font-semibold text-white">
                시상
              </div>
            </div>

            {gameData.prizes.length >
            0 ? (
              <div>
                {gameData.prizes.map(
                  (
                    prize,
                  ) => (
                    <div
                      key={
                        prize.rank
                      }
                      className="
                        flex
                        items-center
                        justify-between
                        gap-3
                        border-b
                        border-white/10
                        py-5
                      "
                    >
                      <div className="whitespace-nowrap text-2xl font-medium text-gray-300">
                        {prize.rank}위
                      </div>

                      <div className="whitespace-nowrap text-3xl font-semibold tabular-nums text-white">
                        {formatNumber(
                          prize.amount,
                        )}

                        <span className="ml-2 text-xl font-normal text-gray-400">
                          LP
                        </span>
                      </div>
                    </div>
                  ),
                )}
              </div>
            ) : (
              <div className="mt-10 text-xl leading-relaxed text-gray-400">
                등록된 시상이 없습니다.
              </div>
            )}
          </aside>

          {/* Center */}
          <main
            className="
              flex
              min-w-0
              flex-col
              px-8
              py-3
            "
          >
            {/* Level + Timer */}
            <section
              className="
                relative
                flex
                min-h-0
                flex-1
                flex-col
                items-center
                justify-center
                pb-1
              "
            >
              <div
                className="
                  mb-1
                  -translate-y-3
                  text-6xl
                  font-bold
                  uppercase
                  tracking-[0.1em]
                  text-gray-200
                "
              >
                {isBreak
                  ? 'BREAK'
                  : `LEVEL ${gameData.currentLevel}`}
              </div>

              {/* 현재 타이머 대폭 확대 */}
              <div
                className="
                  font-black
                  tabular-nums
                  tracking-[-0.075em]
                  text-white
                "
                style={{
                  fontSize:
                    'clamp(16rem, 24vw, 26rem)',
                  lineHeight:
                    0.76,
                }}
              >
                {formatTime(
                  remainingSeconds,
                )}
              </div>

              {isBreak &&
                !isPaused && (
                  <div className="mt-6 rounded-full border border-amber-400/50 bg-amber-400/10 px-10 py-3 text-4xl font-bold text-amber-300">
                    BREAK
                  </div>
                )}

              {isPaused && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#111214]/90">
                  <div className="border border-white/20 bg-[#18191c] px-14 py-8">
                    <div className="text-7xl font-semibold text-white">
                      일시정지
                    </div>
                  </div>
                </div>
              )}

              {isFinished && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#111214]/95">
                  <div className="border border-white/20 bg-[#18191c] px-14 py-8">
                    <div className="text-7xl font-semibold text-white">
                      게임 종료
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Current Blind */}
            <section
              className="
                -translate-y-8
                grid
                shrink-0
                grid-cols-3
                border-y
                border-white/10
              "
            >
              <div className="border-r border-white/10 px-5 py-4 text-center">
                <div className="mb-2 text-3xl font-bold uppercase tracking-[0.08em] text-gray-300">
                  Small Blind
                </div>

                <div
                  className="
                    whitespace-nowrap
                    text-[5.5rem]
                    font-black
                    leading-none
                    tabular-nums
                    text-white
                  "
                >
                  {formatNumber(
                    gameData.currentBlind
                      .smallBlind,
                  )}
                </div>
              </div>

              <div className="border-r border-white/10 px-5 py-4 text-center">
                <div className="mb-2 text-3xl font-bold uppercase tracking-[0.08em] text-gray-300">
                  Big Blind
                </div>

                <div
                  className="
                    whitespace-nowrap
                    text-[5.5rem]
                    font-black
                    leading-none
                    tabular-nums
                    text-white
                  "
                >
                  {formatNumber(
                    gameData.currentBlind
                      .bigBlind,
                  )}
                </div>
              </div>

              <div className="px-5 py-4 text-center">
                <div className="mb-2 text-3xl font-bold uppercase tracking-[0.08em] text-gray-300">
                  Ante
                </div>

                <div
                  className="
                    whitespace-nowrap
                    text-[5.5rem]
                    font-black
                    leading-none
                    tabular-nums
                    text-white
                  "
                >
                  {formatNumber(
                    gameData.currentBlind
                      .ante,
                  )}
                </div>
              </div>
            </section>

            {/* Next Blind */}
            <section
              className="
                -mt-6
                flex
                shrink-0
                items-center
                justify-center
                gap-10
                py-5
              "
            >
              <span
                className="
                  whitespace-nowrap
                  text-4xl
                  font-bold
                  uppercase
                  tracking-[0.08em]
                  text-gray-300
                "
              >
                Next Blind
              </span>

              <span
                className="
                  whitespace-nowrap
                  text-7xl
                  font-black
                  tabular-nums
                  text-white
                "
              >
                {gameData.nextBlind
                  ? `${formatNumber(
                      gameData.nextBlind
                        .smallBlind,
                    )} / ${formatNumber(
                      gameData.nextBlind
                        .bigBlind,
                    )}`
                  : '-'}
              </span>
            </section>
          </main>

          {/* Statistics */}
          <aside className="border-l border-white/10 p-7">
            <div className="mb-5">
              <div className="text-lg font-semibold uppercase tracking-[0.16em] text-gray-400">
                Statistics
              </div>

              <div className="mt-1 text-3xl font-semibold text-white">
                게임 정보
              </div>
            </div>

            <div className="divide-y divide-white/10">
              <StatItem
                label="Players"
                value={`${gameData.nowPeople} / ${gameData.totalPeople}`}
              />

              <StatItem
                label="Rebuy"
                value={formatNumber(
                  gameData.rebuyinCount,
                )}
              />

              <StatItem
                label="Early"
                value={formatNumber(
                  gameData.earlyCount,
                )}
              />

              <StatItem
                label="Overall Time"
                value={getElapsedTime()}
              />

              <StatItem
                label="Total Stack"
                value={formatNumber(
                  gameData.totalStack,
                )}
              />

              <StatItem
                label="Average Stack"
                value={formatNumber(
                  gameData.averageStack,
                )}
              />

              <StatItem
                label="Next Break"
                value={
                  localNextBreakSeconds !==
                  null
                    ? formatTime(
                        localNextBreakSeconds,
                      )
                    : '-'
                }
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default TVDisplay;